
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Download, X, FileImage, FileText } from "lucide-react";

interface FormatSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload: (format: 'jpg' | 'pdf') => void;
  totalRecords: number;
  reportTitle: string;
}

const FormatSelectionDialog = ({ 
  isOpen, 
  onClose, 
  onDownload,
  totalRecords,
  reportTitle
}: FormatSelectionDialogProps) => {
  const [selectedFormat, setSelectedFormat] = useState<'jpg' | 'pdf'>('jpg');

  const handleDownload = () => {
    console.log('Format dialog download clicked:', selectedFormat);
    onDownload(selectedFormat);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  console.log('FormatSelectionDialog render:', { isOpen, totalRecords });

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Print {reportTitle}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
          <DialogDescription>
            Choose format for printing {reportTitle} ({totalRecords} records)
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          <RadioGroup 
            value={selectedFormat} 
            onValueChange={(value) => {
              console.log('Format changed to:', value);
              setSelectedFormat(value as 'jpg' | 'pdf');
            }}
          >
            <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
              <RadioGroupItem value="jpg" id="jpg" />
              <div className="flex items-center space-x-3 flex-1">
                <FileImage className="h-5 w-5 text-blue-600" />
                <div>
                  <Label htmlFor="jpg" className="text-base font-medium cursor-pointer">
                    JPG Image
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    High-quality image format, ultra-fast download
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
              <RadioGroupItem value="pdf" id="pdf" />
              <div className="flex items-center space-x-3 flex-1">
                <FileText className="h-5 w-5 text-red-600" />
                <div>
                  <Label htmlFor="pdf" className="text-base font-medium cursor-pointer">
                    PDF Document
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Professional document format, perfect for printing
                  </p>
                </div>
              </div>
            </div>
          </RadioGroup>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleDownload} disabled={totalRecords === 0}>
              <Download className="w-4 h-4 mr-2" />
              Download {selectedFormat.toUpperCase()}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FormatSelectionDialog;
